import React from 'react'
import { useStatus } from '@sketch-hq/sketch-web-renderer'
import { useLocation } from 'react-router'
import { parse, stringify } from 'query-string'

import { routes } from '@sketch/modules-common'
import { shortcuts } from '@sketch/utils'

import { ReactComponent as ChatBubbleAddIcon } from '@sketch/icons/comment-chat-bubble-plus-17'

import {
  useAnnotationOverlayContext,
  usePlaceDraftAnnotation,
} from '../../hooks'

import ShareSubscriptionStatus from '../../components/ShareSubscriptionStatus'
import CommentTabMessageRestricted from '../../components/CommentTabMessageRestricted'
import VersionRestrictedWarning from '../../components/VersionRestrictedWarning'
import { Share } from '../../types'
import { TooltipShortcut } from '@sketch/components'
import { AddAnnotationButton } from './ShareAnnotations.style'
import AnnotationListContainer from '../AnnotationListContainer'

interface ShareAnnotationsProps {
  share: Share
  showAddAnnotationButton?: boolean
  permitViewingAnnotations?: boolean
}

const ShareAnnotations: React.FCC<ShareAnnotationsProps> = props => {
  const { share, showAddAnnotationButton, permitViewingAnnotations } = props

  const location = useLocation()
  const annotationContext = useAnnotationOverlayContext()
  const isAnnotationContextAvailable = annotationContext?.status === 'available'

  const [isPlacingAnnotation, setPlaceAnnotation] =
    usePlaceDraftAnnotation() || []

  const canvasStatus = useStatus()

  if (!share.commentsEnabled) {
    return <CommentTabMessageRestricted />
  }

  if (!permitViewingAnnotations) {
    return <VersionRestrictedWarning location={location} />
  }

  const queryArguments = parse(location.search)
  const annotation = Array.isArray(queryArguments.annotation)
    ? queryArguments.annotation[0]!
    : queryArguments.annotation || undefined

  const isCanvasLoading = canvasStatus?.type !== 'READY'

  return (
    <AnnotationListContainer
      activeAnnotationIdentifier={annotation}
      isCanvasLoading={isCanvasLoading}
      navbarAction={
        <>
          <ShareSubscriptionStatus shareIdentifier={share.identifier} />

          {showAddAnnotationButton && (
            <AddAnnotationButton
              onClick={(event: MouseEvent) => {
                setPlaceAnnotation?.(!isPlacingAnnotation)
                event.stopPropagation()
              }}
              disabled={
                isCanvasLoading || !annotationContext?.isViewingLatestVersion
              }
              aria-current={isPlacingAnnotation}
              description={
                annotationContext?.isViewingLatestVersion ? (
                  <>
                    Add comment{' '}
                    <TooltipShortcut>
                      Press {shortcuts.addComment.toUpperCase()}
                    </TooltipShortcut>
                  </>
                ) : (
                  <>You can’t add a comment on older document versions.</>
                )
              }
            >
              <span className="sr-only">New comment</span>
              <ChatBubbleAddIcon role="img" />
            </AddAnnotationButton>
          )}
        </>
      }
      resolveAnnotationLink={annotation => {
        /*
         * Assume we're dealing with a *floating annotation* unless the user is
         * on the canvas fallback page (i.e. the canvas has failed to load).
         *
         * TODO: disallow floating annotations for CWVs
         * https://github.com/sketch-hq/Cloud/issues/14254
         */
        if (isAnnotationContextAvailable) {
          return location => ({
            ...location,
            state: {
              isFromCommentTab: true,
            },
            search: stringify({
              ...queryArguments,
              annotation: annotation.identifier,
            }),
          })
        }

        /**
         * Otherwise it should default to the frame
         * view like we have been using.
         *
         * TODO: show non-floating annotations in the document view
         * https://github.com/sketch-hq/Cloud/issues/15430
         */
        if (annotation.currentSubject.__typename === 'Artboard') {
          const frameUUID = annotation.currentSubject.uuid

          return routes.FRAME.create({
            shareID: share.identifier,
            frameUUID,
            query: { ...queryArguments, annotation: annotation.identifier },
          })
        }
      }}
    />
  )
}

export default ShareAnnotations
